import React, { useState, useEffect } from 'react';
import {
    X,
    History,
    Loader2,
    RefreshCw,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Clock,
    Shield,
    FileText,
    Mail,
} from 'lucide-react';
import { apiClient, RecentActivity, BulkOperationSummary, FleetSubtype } from '../../services/api';
import BulkCheckResultsModal from './BulkCheckResultsModal';

interface DriversHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    subtype?: FleetSubtype;
}

type Tab = 'checks' | 'operations';

const DriversHistoryModal: React.FC<DriversHistoryModalProps> = ({ isOpen, onClose, subtype }) => {
    const [activeTab, setActiveTab] = useState<Tab>('checks');

    // Licence checks state (single + bulk via getRecentActivity)
    const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
    const [isLoadingChecks, setIsLoadingChecks] = useState(false);
    const [checksError, setChecksError] = useState<string | null>(null);

    // Bulk operations state (consent sends, imports)
    const [operations, setOperations] = useState<BulkOperationSummary[]>([]);
    const [isLoadingOps, setIsLoadingOps] = useState(false);
    const [opsError, setOpsError] = useState<string | null>(null);

    // Results modal
    const [selectedOperationId, setSelectedOperationId] = useState<string | null>(null);
    const [operationResults, setOperationResults] = useState<any>(null);
    const [isLoadingResults, setIsLoadingResults] = useState(false);
    const [showResultsModal, setShowResultsModal] = useState(false);

    const loadChecks = async () => {
        setIsLoadingChecks(true);
        setChecksError(null);
        try {
            const data = await apiClient.getRecentActivity();
            setRecentActivity(data);
        } catch (err: any) {
            setChecksError(err.message || 'Failed to load check history');
        } finally {
            setIsLoadingChecks(false);
        }
    };

    const loadOperations = async () => {
        setIsLoadingOps(true);
        setOpsError(null);
        try {
            const response = await apiClient.getBulkOperations({ status: 'completed', limit: 50 });
            setOperations(response.data);
        } catch (err: any) {
            setOpsError(err.message || 'Failed to load operations history');
        } finally {
            setIsLoadingOps(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            loadChecks();
            loadOperations();
        }
    }, [isOpen]);

    const handleViewResults = async (operationId: string) => {
        setSelectedOperationId(operationId);
        setIsLoadingResults(true);
        try {
            const results = await apiClient.getBulkOperationResults(operationId);
            setOperationResults(results);
            setShowResultsModal(true);
        } catch (err: any) {
            // handle silently
        } finally {
            setIsLoadingResults(false);
        }
    };

    if (!isOpen) return null;

    const getRiskBadge = (riskLevel: string) => {
        switch (riskLevel) {
            case 'high':
                return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"><AlertTriangle className="w-3 h-3 mr-1" />High</span>;
            case 'medium':
                return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800"><AlertTriangle className="w-3 h-3 mr-1" />Medium</span>;
            default:
                return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"><Shield className="w-3 h-3 mr-1" />Low</span>;
        }
    };

    const getOperationTypeLabel = (type: string) => {
        switch (type) {
            case 'licence_check': return { label: 'Licence Check', icon: <CheckCircle className="w-4 h-4 text-green-600" /> };
            case 'consent_send': return { label: 'Consent Send', icon: <Mail className="w-4 h-4 text-blue-600" /> };
            case 'driver_import': return { label: 'Driver Import', icon: <FileText className="w-4 h-4 text-purple-600" /> };
            default: return { label: type, icon: <Clock className="w-4 h-4 text-gray-500" /> };
        }
    };

    const formatDate = (date: Date | string | null) => {
        if (!date) return '—';
        return new Date(date).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center">
                <div className="fixed inset-0 bg-black/50" onClick={onClose} />
                <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-gray-100 rounded-lg">
                                <History className="w-5 h-5 text-gray-700" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">History</h2>
                                <p className="text-sm text-gray-500">Licence checks, consent sends, and imports</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="flex border-b border-gray-200 px-6 flex-shrink-0">
                        <button
                            onClick={() => setActiveTab('checks')}
                            className={`py-3 px-1 mr-6 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === 'checks'
                                    ? 'border-green-500 text-green-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Licence Checks
                        </button>
                        <button
                            onClick={() => setActiveTab('operations')}
                            className={`py-3 px-1 mr-6 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === 'operations'
                                    ? 'border-green-500 text-green-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Bulk Operations
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto">
                        {/* Licence Checks Tab */}
                        {activeTab === 'checks' && (
                            <div>
                                <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                                    <p className="text-xs text-gray-500">All individual and bulk licence checks — most recent first</p>
                                    <button
                                        onClick={loadChecks}
                                        disabled={isLoadingChecks}
                                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
                                        title="Refresh"
                                    >
                                        <RefreshCw className={`w-4 h-4 ${isLoadingChecks ? 'animate-spin' : ''}`} />
                                    </button>
                                </div>

                                {isLoadingChecks ? (
                                    <div className="flex items-center justify-center py-16">
                                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                                    </div>
                                ) : checksError ? (
                                    <div className="p-6 text-center text-red-600 text-sm">{checksError}</div>
                                ) : recentActivity.length === 0 ? (
                                    <div className="p-8 text-center">
                                        <Clock className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                                        <p className="text-sm text-gray-500">No licence checks found</p>
                                    </div>
                                ) : (
                                    <table className="min-w-full divide-y divide-gray-100">
                                        <thead className="bg-white sticky top-0">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Driver</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Licence</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Checked By</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-50">
                                            {recentActivity.map((activity) => (
                                                <tr key={activity.id} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-6 py-3 text-sm font-medium text-gray-900">{activity.driverName}</td>
                                                    <td className="px-6 py-3 text-sm text-gray-600 font-mono">{activity.licenceNumber}</td>
                                                    <td className="px-6 py-3 text-sm text-gray-500 whitespace-nowrap">
                                                        {formatDate(activity.checkDate)}
                                                    </td>
                                                    <td className="px-6 py-3">{getRiskBadge(activity.riskLevel)}</td>
                                                    <td className="px-6 py-3 text-sm text-gray-600">{activity.status}</td>
                                                    <td className="px-6 py-3 text-sm text-gray-500">{activity.checkedBy}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        )}

                        {/* Bulk Operations Tab */}
                        {activeTab === 'operations' && (
                            <div>
                                <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                                    <p className="text-xs text-gray-500">Bulk licence checks, consent sends, and CSV imports — most recent first</p>
                                    <button
                                        onClick={loadOperations}
                                        disabled={isLoadingOps}
                                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
                                        title="Refresh"
                                    >
                                        <RefreshCw className={`w-4 h-4 ${isLoadingOps ? 'animate-spin' : ''}`} />
                                    </button>
                                </div>

                                {isLoadingOps ? (
                                    <div className="flex items-center justify-center py-16">
                                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                                    </div>
                                ) : opsError ? (
                                    <div className="p-6 text-center text-red-600 text-sm">{opsError}</div>
                                ) : operations.length === 0 ? (
                                    <div className="p-8 text-center">
                                        <Clock className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                                        <p className="text-sm text-gray-500">No bulk operations found</p>
                                    </div>
                                ) : (
                                    <table className="min-w-full divide-y divide-gray-100">
                                        <thead className="bg-white sticky top-0">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Drivers</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Successful</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Failed</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-50">
                                            {operations.map((op) => {
                                                const typeInfo = getOperationTypeLabel(op.type);
                                                return (
                                                    <tr key={op.id} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-6 py-3">
                                                            <div className="flex items-center space-x-2">
                                                                {typeInfo.icon}
                                                                <span className="text-sm font-medium text-gray-900">{typeInfo.label}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-3 text-sm text-gray-500 whitespace-nowrap">
                                                            {formatDate(op.completedAt)}
                                                        </td>
                                                        <td className="px-6 py-3 text-sm text-gray-900">{op.totalItems}</td>
                                                        <td className="px-6 py-3">
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                                <CheckCircle className="w-3 h-3 mr-1" />{op.successCount}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-3">
                                                            {op.failureCount > 0 ? (
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                                                    <XCircle className="w-3 h-3 mr-1" />{op.failureCount}
                                                                </span>
                                                            ) : (
                                                                <span className="text-xs text-gray-400">—</span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-3">
                                                            {op.type === 'licence_check' && (
                                                                <button
                                                                    onClick={() => handleViewResults(op.id)}
                                                                    disabled={isLoadingResults && selectedOperationId === op.id}
                                                                    className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                                                >
                                                                    {isLoadingResults && selectedOperationId === op.id ? (
                                                                        <Loader2 className="w-3 h-3 animate-spin inline" />
                                                                    ) : 'View Results'}
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Bulk Check Results Modal */}
            <BulkCheckResultsModal
                isOpen={showResultsModal}
                onClose={() => {
                    setShowResultsModal(false);
                    setOperationResults(null);
                    setSelectedOperationId(null);
                }}
                results={operationResults}
                subtype={subtype}
            />
        </>
    );
};

export default DriversHistoryModal;
